import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CreatePaymentMethodData } from '@stripe/stripe-js';
import Stripe from 'stripe';
import { SubscriptionDTO } from '../../../api/subscriptions/subscribe';
import { RegisterMiembroDTO } from '../../../api/users/registerMiembro';
import { APIError } from '../../../types/api/api';
import { UUID } from '../../../types/standard';
import { Miembro } from '../../../types/users/users';
import { t } from '../../../i18n';

export interface SocioTipo {
  socio_tipo: 'organizacion' | 'cooperativa';
  price: Stripe.Price | null;
  organizacion_id?: UUID;
}

export type RegisterStep = 'register' | 'completado';

export interface MiembroSocio {
  es_socio: boolean | undefined;
  datos_socio: SocioTipo | undefined;
  setup_intent: Stripe.SetupIntent | null;
  payment_method: Stripe.PaymentMethod | null;
  loading_setup_intent: boolean;
  subscription: SubscriptionDTO | null;
  errors: APIError | null;
}

export type RegisterMiembroState = {
  form_data: RegisterMiembroDTO | null;
  miembro: Miembro | null;
  socio: MiembroSocio;
  voluntario: boolean | undefined;
  persona_cuidadora: boolean | undefined;
  loading_register: boolean;
  errors: APIError | null;
  registro_completado: boolean;
  step: RegisterStep;
};

const initialState: RegisterMiembroState = {
  form_data: null,
  miembro: null,
  socio: {
    es_socio: undefined,
    datos_socio: undefined,
    setup_intent: null,
    payment_method: null,
    loading_setup_intent: false,
    errors: null,
    subscription: null,
  },
  voluntario: undefined,
  persona_cuidadora: undefined,
  errors: null,
  loading_register: false,
  registro_completado: false,
  step: 'register',
};

const registerMiembroSlice = createSlice({
  name: 'registerMiembro',
  initialState: initialState,
  reducers: {
    reset() {
      return { ...initialState };
    },
    register(state, action: PayloadAction<RegisterMiembroDTO>) {
      state.loading_register = true;
      state.form_data = action.payload;
    },
    registerOk(state, action: PayloadAction<Miembro>) {
      state.miembro = action.payload;
      state.loading_register = false;
      state.step = 'completado';
    },
    registerKo(state, action: PayloadAction<APIError>) {
      state.loading_register = false;
      state.errors = action.payload;
      state.step = 'register';
    },
    createSetupIntent(state) {
      if (!state.miembro) {
        state.socio.errors = {
          errorType: 'validation',
          message: t('RoutesHomeHomeConfiguraMetodoPagoRegistroNecesario'),
          errors: { organizacion_id: ['Se requiere un miembro/a'] },
        };
      } else {
        if (!state.socio.setup_intent) {
          state.socio.loading_setup_intent = true;
        }
      }
    },
    createSetupIntentOk(state, action: PayloadAction<Stripe.SetupIntent>) {
      state.socio.loading_setup_intent = false;
      state.socio.setup_intent = action.payload;
      state.socio.errors = null;
    },
    createSetupIntentKo(state, action: PayloadAction<APIError>) {
      state.socio.loading_setup_intent = false;
      state.socio.setup_intent = null;
      state.socio.errors = action.payload;
    },
    confirmSetupIntent(state, data: PayloadAction<Object | undefined>) {
      state.socio.loading_setup_intent = true;
      state.socio.errors = null;
    },
    confirmSetupIntentOk(state, action: PayloadAction<Stripe.SetupIntent>) {
      state.socio.setup_intent = action.payload;
      state.socio.loading_setup_intent = false;
    },
    confirmSetupIntentKo(state, action: PayloadAction<APIError>) {
      state.socio.loading_setup_intent = false;
      state.socio.errors = action.payload;
    },
    storeSuscriptionOk(state, action: PayloadAction<SubscriptionDTO>) {
      state.socio.loading_setup_intent = false;
      state.socio.subscription = action.payload;
      state.step = 'completado';
    },
    storeSuscriptionKo(state, action: PayloadAction<APIError>) {
      state.socio.errors = action.payload;
      state.socio.loading_setup_intent = false;
    },
    updateSetupIntentOk(state, action: PayloadAction<Stripe.SetupIntent>) {
      state.socio.setup_intent = action.payload;
      state.socio.errors = null;
    },
    updateSetupIntentKo(state, action: PayloadAction<APIError>) {
      state.socio.loading_setup_intent = false;
      state.socio.errors = action.payload;
    },
    createPaymentMethod(state, action: PayloadAction<CreatePaymentMethodData>) {
      state.socio.payment_method = null;
      state.socio.errors = null;
    },
    createPaymentMethodOk(state, action: PayloadAction<Stripe.PaymentMethod>) {
      state.socio.loading_setup_intent = true;
      state.socio.payment_method = action.payload;
    },
    createPaymentMethodKo(state, action: PayloadAction<APIError>) {
      state.socio.loading_setup_intent = false;
      state.socio.payment_method = null;
      state.socio.errors = action.payload;
    },
    finishRegister(state) {
      state.loading_register = true;
    },
    storeStep(state, action: PayloadAction<RegisterStep>) {
      state.step = action.payload;
    },
  },
});

export const {
  register,
  reset,
  createSetupIntent,
  confirmSetupIntent,
  createPaymentMethod,
  storeStep,
} = registerMiembroSlice.actions;

export default registerMiembroSlice;
